import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReconciliationExceptionsPanel = ({ exceptions, onResolve, loading }) => {
  const getSeverityIcon = (severity) => {
    const icons = {
      low: 'Info',
      medium: 'AlertCircle',
      high: 'AlertTriangle',
      critical: 'XCircle'
    };
    return icons?.[severity] || 'AlertCircle';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'text-info',
      medium: 'text-warning',
      high: 'text-destructive',
      critical: 'text-destructive'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const bgs = {
      low: 'bg-info/10 border-info/20',
      medium: 'bg-warning/10 border-warning/20',
      high: 'bg-destructive/10 border-destructive/20',
      critical: 'bg-destructive/20 border-destructive/40'
    };
    return bgs?.[severity] || 'bg-muted border-border';
  };

  const getTypeLabel = (type) => {
    const labels = {
      amount_mismatch: 'Amount Mismatch',
      missing_transaction: 'Missing Transaction',
      duplicate_entry: 'Duplicate Entry',
      payment_failed: 'Payment Failed',
      refund_pending: 'Refund Pending'
    };
    return labels?.[type] || type;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-48">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Reconciliation Exceptions</h3>
          <p className="text-sm text-muted-foreground">Critical alerts requiring immediate attention</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-destructive animate-pulse"></div>
          <span className="text-sm font-medium text-destructive">
            {exceptions?.filter(e => e?.status === 'open')?.length} Open
          </span>
        </div>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {exceptions?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-3 text-success" />
            <p className="font-medium text-foreground">All Clear!</p>
            <p className="text-sm">No exceptions found</p>
          </div>
        ) : (
          exceptions?.map((exception) => (
            <div
              key={exception?.id}
              className={`border rounded-lg p-4 ${getSeverityBg(exception?.severity)}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${getSeverityColor(exception?.severity)}`}>
                  <Icon name={getSeverityIcon(exception?.severity)} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{getTypeLabel(exception?.exception_type)}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          exception?.severity === 'critical' || exception?.severity === 'high' ?'bg-destructive/20 text-destructive' :'bg-warning/20 text-warning'
                        }`}>
                          {exception?.severity?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{exception?.description}</p>
                    </div>
                  </div>

                  {(exception?.expected_amount || exception?.actual_amount) && (
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      {exception?.expected_amount && (
                        <div>
                          <span className="text-muted-foreground">Expected: </span>
                          <span className="font-semibold text-foreground">${parseFloat(exception?.expected_amount)?.toFixed(2)}</span>
                        </div>
                      )}
                      {exception?.actual_amount && (
                        <div>
                          <span className="text-muted-foreground">Actual: </span>
                          <span className="font-semibold text-foreground">${parseFloat(exception?.actual_amount)?.toFixed(2)}</span>
                        </div>
                      )}
                      {exception?.expected_amount && exception?.actual_amount && (
                        <div>
                          <span className="text-destructive font-semibold">
                            Difference: ${Math.abs(exception?.expected_amount - exception?.actual_amount)?.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {new Date(exception?.created_at)?.toLocaleString()}
                    </span>
                    {exception?.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onResolve?.(exception?.id)}
                        iconName="Check"
                        iconPosition="left"
                      >
                        Resolve
                      </Button>
                    )}
                    {exception?.status === 'resolved' && (
                      <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                        ✓ Resolved
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReconciliationExceptionsPanel;